import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { BaseButton } from "@ui/atoms/BaseButton/BaseButton";
import { BaseDialog } from "@ui/atoms/BaseDialog/BaseDialog";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import { ReactNode } from "react";

export interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  onClose?: () => void;
  cancelButtonText?: string;
  message?: string;
  buttonText?: string;
  intent?: "primary" | "success" | "error" | "warning";
  icon?: ReactNode;
  cancelButtonIcon?: ReactNode;
  confirmButtonIcon?: ReactNode;
  children?: ReactNode;
  loading?: boolean;
  /** When true, the primary (confirm) button is disabled but the cancel button stays enabled. */
  confirmButtonDisabled?: boolean;
}

export const ConfirmationDialog = ({
  buttonText = "Confirm",
  cancelButtonIcon,
  cancelButtonText = "Cancel",
  children,
  confirmButtonDisabled = false,
  confirmButtonIcon,
  icon,
  intent = "error",
  loading = false,
  message,
  onCancel,
  onClose,
  onConfirm,
  open,
  title
}: ConfirmationDialogProps) => {
  return (
    <BaseDialog
      open={open}
      // Not while the confirm is in flight: the backdrop and Escape would
      // otherwise close it under a request that is still running.
      onClose={loading ? undefined : (onClose ?? onCancel)}
      // BaseDialog only renders primary/error; map the wider button intents down
      intent={intent === "error" ? "error" : "primary"}
      title={title}
      icon={icon}
    >
      {/* 14 above: the title and the message read as one block. */}
      <DialogContent sx={{ padding: "14px 24px 20px" }}>
        {children ? (
          children
        ) : (
          <BaseTypography
            text={message}
            variant="body2"
            weight="regular"
            sx={{ whiteSpace: "pre-line" }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1, pb: "20px" }}>
        <BaseButton
          dataTestId="confirm-dialog-button-cancel"
          disabled={loading}
          loading={loading}
          color="secondary"
          text={cancelButtonText}
          onClick={onCancel}
          fullWidth
          startIcon={cancelButtonIcon}
          sx={{ flex: 1 }}
        />
        <BaseButton
          dataTestId="confirm-dialog-button-ok"
          disabled={loading || confirmButtonDisabled}
          loading={loading}
          color={intent}
          text={buttonText}
          onClick={onConfirm}
          fullWidth
          startIcon={confirmButtonIcon}
          sx={{ flex: 1 }}
        />
      </DialogActions>
    </BaseDialog>
  );
};
